import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tier, setTier] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      navigate("/app/billing", { replace: true });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          setTier(r.data.tier);
          // Refresh user in context
          const me = await api.get("/auth/me");
          localStorage.setItem("ae_user", JSON.stringify(me.data));
          toast.success(`Upgraded to ${r.data.tier?.toUpperCase()}`);
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < 8) {
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 4) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, navigate]);

  return (
    <div className="max-w-md mx-auto py-16 text-center">
      {status === "checking" && (
        <div data-testid="billing-checking">
          <Loader2 className="h-10 w-10 text-emerald-400 mx-auto animate-spin" />
          <h2 className="font-heading text-2xl mt-4">Confirming your payment…</h2>
          <p className="text-sm text-slate-400 mt-2">This usually takes 2-5 seconds.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="billing-success">
          <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto" />
          <h2 className="font-heading text-3xl mt-4 tracking-tight">You're on {tier?.toUpperCase()}.</h2>
          <p className="text-sm text-slate-400 mt-2">All features are now unlocked for your team.</p>
          <Link to="/app/dashboard" className="btn-primary text-sm mt-6 inline-block">Back to dashboard</Link>
        </div>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <div data-testid="billing-failed">
          <h2 className="font-heading text-2xl text-rose-400">{status === "expired" ? "Session expired" : "Payment not confirmed"}</h2>
          <p className="text-sm text-slate-400 mt-2">No charges were made. You can retry from the billing page.</p>
          <Link to="/app/billing" className="btn-ghost text-sm mt-6 inline-block">Back to billing</Link>
        </div>
      )}
    </div>
  );
}
